import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip, IconButton, Tooltip,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormGroup, FormControlLabel, Checkbox,
  Menu, MenuItem, Switch, FormControl, InputLabel, Select, Collapse, Divider, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from '@dnd-kit/core';
import { adminClient } from '../data/adminClient';
import { checkSchedule, autoSuggest, runCapacity, isSchedulable } from '../../shared/workshopScheduler';
import { ALLOWED_SESSIONS_LABELS, isConfirmedStatus } from '../../shared/workshopOptions';
import { useEventReadOnly } from '../adminAccess';

const sessionLabel = (s) => `${s.name}${s.time ? ` · ${s.time}` : ''}`;
const toRun = (r) => ({ workshopId: r.workshopId, session: r.session, spaceId: r.spaceId, capacityOverride: r.capacityOverride || null });

// Admin › Workshops › Schedule. Drag workshops into a space × session grid.
// Rules are checked live (see shared/workshopScheduler.js); Auto-suggest
// fills the grid following them. Nothing is saved until "Save schedule".
export default function ScheduleSection() {
  // View-only admin: the schedule is shown, but nothing can be dragged,
  // removed, opened for rules, or saved.
  const readOnly = useEventReadOnly();
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null); // { severity, text, list? }
  const [suggestAnchor, setSuggestAnchor] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rulesFor, setRulesFor] = useState(null); // { workshop, run? }
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await adminClient.getWorkshopSchedule();
      setData(d);
      setRuns(d.runs.map(toRun));
      setDirty(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sessionNames = useMemo(() => (data ? { 1: data.sessions[1].name, 2: data.sessions[2].name } : {}), [data]);
  const check = useMemo(() => (data
    ? checkSchedule({ workshops: data.workshops, spaces: data.spaces, runs, rules: data.rules, dancerCount: data.dancerCount, sessionNames })
    : null), [data, runs, sessionNames]);

  const update = (next) => { setRuns(next); setDirty(true); };

  // ── Drag and drop ──
  // Draggable ids: "pool|<workshopId>" or "run|<session>|<spaceId>".
  // Droppable ids: "cell|<session>|<spaceId>" or "pool".
  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    const [kind, a, b] = String(active.id).split('|');
    const target = String(over.id);
    const occupantAt = (session, spaceId) => runs.find(r => r.session === session && r.spaceId === spaceId);

    if (kind === 'run') {
      const fromSession = Number(a); const fromSpace = b;
      const moving = occupantAt(fromSession, fromSpace);
      if (!moving) return;
      if (target === 'pool') { update(runs.filter(r => r !== moving)); return; }
      const [, ts, tsp] = target.split('|');
      const toSession = Number(ts);
      if (toSession === fromSession && tsp === fromSpace) return;
      const occupant = occupantAt(toSession, tsp);
      const clash = (w, session, ignore) => runs.some(r => r !== ignore && r !== moving && r !== occupant && r.workshopId === w && r.session === session);
      if (clash(moving.workshopId, toSession) || (occupant && clash(occupant.workshopId, fromSession))) {
        setNotice({ severity: 'warning', text: 'That would put the same workshop in one session twice.' });
        return;
      }
      update(runs.map(r => {
        if (r === moving) return { ...r, session: toSession, spaceId: tsp };
        if (r === occupant) return { ...r, session: fromSession, spaceId: fromSpace };
        return r;
      }));
      return;
    }

    if (kind === 'pool' && target.startsWith('cell|')) {
      const [, ts, tsp] = target.split('|');
      const session = Number(ts);
      // Replaces whatever was in the cell (it goes back to unplaced), and
      // moves this workshop if it was already in that session elsewhere.
      const next = runs.filter(r => !(r.session === session && r.spaceId === tsp) && !(r.session === session && r.workshopId === a));
      update([...next, { workshopId: a, session, spaceId: tsp, capacityOverride: null }]);
    }
  };

  const runSuggest = (mode) => {
    setSuggestAnchor(null);
    const { runs: suggested, unplaced } = autoSuggest({ workshops: data.workshops, spaces: data.spaces, runs, rules: data.rules, mode });
    update(suggested.map(toRun));
    setNotice(unplaced.length
      ? { severity: 'warning', text: `Suggested a schedule, but ${unplaced.length} workshop placement${unplaced.length === 1 ? '' : 's'} couldn’t fit. Review it, then save.`, list: unplaced.map(u => `${u.workshop.title} (${u.workshop.leaderName}) — ${u.reason}`) }
      : { severity: 'success', text: 'Suggested a schedule that follows all the rules. Review it, then Save schedule.' });
  };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await adminClient.saveWorkshopSchedule(runs);
      setNotice({ severity: 'success', text: 'Schedule saved.' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!data) return error ? <Alert severity="error">{error}</Alert> : null;

  const schedulable = data.workshops.filter(isSchedulable);
  const declined = data.workshops.filter(w => !isSchedulable(w));
  const pool = schedulable.filter(w => (check.remaining.get(w.workshopId) || 0) > 0);
  const errors = check.issues.filter(i => i.severity === 'error');
  const warnings = check.issues.filter(i => i.severity === 'warning');
  const issuesByCell = new Map();
  check.issues.forEach(i => i.cells.forEach(c => issuesByCell.set(c, [...(issuesByCell.get(c) || []), i])));
  const wById = new Map(data.workshops.map(w => [w.workshopId, w]));

  return (
    <>
      {/* Summary + actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {[1, 2].map(s => {
          const short = data.dancerCount > 0 && check.seats[s] < data.dancerCount;
          return (
            <Tooltip key={s} title={`Every dancer needs a seat in each session (${data.dancerCount} dancers registered).`}>
              <Chip size="small" color={short ? 'warning' : 'success'} variant="outlined"
                label={`${data.sessions[s].name}: ${check.seats[s]} / ${data.dancerCount} seats`} />
            </Tooltip>
          );
        })}
        <Chip size="small" color={errors.length ? 'error' : 'default'} variant="outlined" label={`${errors.length} problem${errors.length === 1 ? '' : 's'}`} />
        <Chip size="small" color={warnings.length ? 'warning' : 'default'} variant="outlined" label={`${warnings.length} warning${warnings.length === 1 ? '' : 's'}`} />
        {dirty && <Chip size="small" color="info" label="Unsaved changes" />}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload (discards unsaved changes)"><span><IconButton size="small" onClick={load} disabled={loading || saving}><RefreshIcon fontSize="small" /></IconButton></span></Tooltip>
        {!readOnly && <Button size="small" variant="outlined" startIcon={<SettingsIcon />} onClick={() => setSettingsOpen(o => !o)}>Sessions & rules</Button>}
        {!readOnly && <Button size="small" variant="outlined" startIcon={<AutoFixHighIcon />} onClick={e => setSuggestAnchor(e.currentTarget)}
          disabled={!data.spaces.length || !schedulable.length}>Auto-suggest</Button>}
        <Menu anchorEl={suggestAnchor} open={!!suggestAnchor} onClose={() => setSuggestAnchor(null)}>
          <MenuItem onClick={() => runSuggest('fill')}>Fill the empty spots (keep what’s placed)</MenuItem>
          <MenuItem onClick={() => runSuggest('fresh')}>Start over (suggest the whole schedule)</MenuItem>
        </Menu>
        {!readOnly && <Button size="small" variant="contained" onClick={save} disabled={!dirty || saving}>{saving ? <CircularProgress size={18} color="inherit" /> : 'Save schedule'}</Button>}
      </Box>

      <Collapse in={settingsOpen}>
        <SettingsPanel data={data} onSaved={(s) => { setData(d => ({ ...d, ...s })); setSettingsOpen(false); }} />
      </Collapse>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
          {notice.list?.length > 0 && <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>{notice.list.map((t, i) => <li key={i}>{t}</li>)}</Box>}
        </Alert>
      )}

      {data.spaces.length === 0 ? (
        <Alert severity="info">Add your spaces on the Spaces page first, then come back to build the schedule.</Alert>
      ) : (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          {/* Nothing left to place: the list collapses to a bar (still a drop target for unscheduling). */}
          {pool.length === 0 && (
            <PoolDrop sx={{ mb: 2, py: 1, position: 'static' }}>
              <Typography variant="body2">
                {schedulable.length ? '✓ Every workshop is on the schedule.' : 'No workshops yet — add leaders and their workshops on the Workshop Leaders page.'}
                {!readOnly && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  Drag a placed workshop here (or use its ×) to unschedule it.
                </Typography>}
              </Typography>
              {declined.length > 0 && (
                <Typography variant="caption" color="text.disabled" display="block">
                  Declined (not scheduled): {declined.map(w => `${w.title} — ${w.leaderName}`).join(', ')}
                </Typography>
              )}
            </PoolDrop>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: pool.length ? '260px 1fr' : '1fr' }, gap: 2, alignItems: 'start' }}>
            {/* Unplaced */}
            {pool.length > 0 && (
            <PoolDrop>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>To schedule ({check.toSchedule})</Typography>
              {pool.map(w => (
                <PoolCard key={w.workshopId} workshop={w} remaining={check.remaining.get(w.workshopId)}
                  options={schedulable.filter(x => x.leaderId === w.leaderId).length} readOnly={readOnly} onClick={readOnly ? undefined : () => setRulesFor({ workshop: w })} />
              ))}
              {!readOnly && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Drag onto the grid. Drag a placed workshop back here to unschedule it.
                Leaders who aren’t confirmed yet can be scheduled too — they’re marked until you confirm them.
              </Typography>}
              {declined.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Declined (not scheduled):</Typography>
                  {declined.map(w => (
                    <Typography key={w.workshopId} variant="caption" display="block" color="text.disabled">{w.title} — {w.leaderName} ({w.leaderStatus})</Typography>
                  ))}
                </>
              )}
            </PoolDrop>
            )}

            {/* Grid */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 170 }}>Space</TableCell>
                    {[1, 2].map(s => <TableCell key={s}><Typography variant="body2" fontWeight={600}>{sessionLabel(data.sessions[s])}</Typography></TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.spaces.map(sp => (
                    <TableRow key={sp.spaceId}>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography variant="body2" fontWeight={500}>{sp.name}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">Up to {sp.maxDancers}</Typography>
                        {sp.equipment.length > 0 && <Typography variant="caption" color="text.disabled" display="block">{sp.equipment.join(', ')}</Typography>}
                      </TableCell>
                      {[1, 2].map(s => {
                        const run = runs.find(r => r.session === s && r.spaceId === sp.spaceId);
                        const w = run && wById.get(run.workshopId);
                        return (
                          <CellDrop key={s} id={`cell|${s}|${sp.spaceId}`} disabled={readOnly}>
                            {w ? (
                              <RunCard id={`run|${s}|${sp.spaceId}`} workshop={w} capacity={runCapacity(w, sp, run.capacityOverride)}
                                overridden={!!run.capacityOverride} issues={issuesByCell.get(`${s}|${sp.spaceId}`) || []}
                                readOnly={readOnly}
                                onRemove={() => update(runs.filter(r => r !== run))}
                                onClick={readOnly ? undefined : () => setRulesFor({ workshop: w, run })} />
                            ) : null}
                          </CellDrop>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DndContext>
      )}

      {/* All issues */}
      {check.issues.length > 0 && data.spaces.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 2, p: 1.5 }}>
          <Typography variant="body2" fontWeight={600} sx={{ px: 1 }}>Checks</Typography>
          <List dense disablePadding>
            {[...errors, ...warnings].map((i, idx) => (
              <ListItem key={idx} sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>{i.severity === 'error' ? <ErrorOutlineIcon fontSize="small" color="error" /> : <WarningAmberIcon fontSize="small" color="warning" />}</ListItemIcon>
                <ListItemText primary={i.message} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {rulesFor && (
        <RulesDialog workshop={rulesFor.workshop} run={rulesFor.run} spaces={data.spaces} sessions={data.sessions}
          onClose={() => setRulesFor(null)}
          onSavedRules={(fields) => setData(d => ({ ...d, workshops: d.workshops.map(w => (w.workshopId === rulesFor.workshop.workshopId ? { ...w, ...fields } : w)) }))}
          onOverride={(capacityOverride) => update(runs.map(r => (r === rulesFor.run ? { ...r, capacityOverride } : r)))} />
      )}
    </>
  );
}

function PoolDrop({ children, sx }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'pool' });
  return (
    <Paper ref={setNodeRef} variant="outlined" sx={{ p: 1.5, bgcolor: isOver ? 'action.hover' : 'background.paper', position: { md: 'sticky' }, top: 16, ...sx }}>
      {children}
    </Paper>
  );
}

function CellDrop({ id, children, disabled }) {
  const { setNodeRef, isOver } = useDroppable({ id, disabled });
  return (
    <TableCell ref={setNodeRef} sx={{ verticalAlign: 'top', bgcolor: isOver ? 'primary.50' : undefined, outline: isOver ? '2px dashed' : 'none', outlineColor: 'primary.main', height: 72 }}>
      {children || <Typography variant="caption" color="text.disabled">{disabled ? '—' : 'Drop a workshop here'}</Typography>}
    </TableCell>
  );
}

function dragStyle(transform, isDragging, readOnly) {
  if (readOnly) return {};
  return {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : 1,
    cursor: 'grab',
    touchAction: 'none',
  };
}

// options = how many workshops this leader has; any one can fill their spot.
function PoolCard({ workshop: w, remaining, options, onClick, readOnly }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `pool|${w.workshopId}`, disabled: readOnly });
  return (
    <Paper ref={setNodeRef} {...attributes} {...listeners} onClick={onClick} variant="outlined"
      sx={{ p: 1, mb: 1, position: 'relative', boxShadow: isDragging ? 4 : 0 }} style={dragStyle(transform, isDragging, readOnly)}>
      <Typography variant="body2" fontWeight={600} sx={{ pr: 3 }}>{w.title}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">{[w.leaderName, w.style].filter(Boolean).join(' · ')}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">{ALLOWED_SESSIONS_LABELS[w.allowedSessions]}</Typography>
      {options > 1 && (
        <Typography variant="caption" color="text.secondary" display="block">
          One of {options} workshop options — {remaining > 1 ? `${remaining} spots to fill` : '1 spot to fill'}
        </Typography>
      )}
      <StatusChip workshop={w} />
      {remaining > 1 && options === 1 && <Chip size="small" label={`×${remaining}`} sx={{ position: 'absolute', top: 6, right: 6, height: 18 }} />}
    </Paper>
  );
}

// Marks a leader who isn't confirmed yet (nothing for confirmed ones).
function StatusChip({ workshop: w }) {
  if (isConfirmedStatus(w.leaderStatus)) return null;
  return (
    <Chip size="small" variant="outlined" color={w.leaderStatus === 'Declined' ? 'error' : 'warning'}
      label={w.leaderStatus === 'Declined' ? 'Declined' : `Not confirmed · ${w.leaderStatus}`} sx={{ mt: 0.5, height: 20, fontSize: 11 }} />
  );
}

function RunCard({ id, workshop: w, capacity, overridden, issues, onRemove, onClick, readOnly }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id, disabled: readOnly });
  const hasError = issues.some(i => i.severity === 'error');
  const hasWarning = issues.length > 0;
  return (
    <Paper ref={setNodeRef} {...attributes} {...listeners} onClick={onClick} variant="outlined"
      sx={{ p: 1, position: 'relative', borderColor: hasError ? 'error.main' : hasWarning ? 'warning.main' : 'divider', boxShadow: isDragging ? 4 : 0 }}
      style={dragStyle(transform, isDragging, readOnly)}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, pr: 3 }}>
        {hasWarning && (
          <Tooltip title={<Box>{issues.map((i, k) => <div key={k}>• {i.message}</div>)}</Box>}>
            {hasError ? <ErrorOutlineIcon fontSize="small" color="error" /> : <WarningAmberIcon fontSize="small" color="warning" />}
          </Tooltip>
        )}
        <Typography variant="body2" fontWeight={600}>{w.title}</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block">{[w.leaderName, w.style].filter(Boolean).join(' · ')}</Typography>
      <Typography variant="caption" color={overridden ? 'primary' : 'text.secondary'} display="block">{capacity} seats{overridden ? ' (limit set)' : ''}</Typography>
      <StatusChip workshop={w} />
      {!readOnly && (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(); }} onPointerDown={e => e.stopPropagation()}
          sx={{ position: 'absolute', top: 2, right: 2 }} aria-label="Unschedule">
          <CloseIcon fontSize="inherit" />
        </IconButton>
      )}
    </Paper>
  );
}

// Session names/times and which optional rules to check.
function SettingsPanel({ data, onSaved }) {
  const [sessions, setSessions] = useState(data.sessions);
  const [rules, setRules] = useState(data.rules);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const setS = (n, key) => (e) => setSessions(s => ({ ...s, [n]: { ...s[n], [key]: e.target.value } }));

  const save = async () => {
    setBusy(true); setError(null);
    try { onSaved(await adminClient.saveScheduleSettings({ sessions, rules })); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {[1, 2].map(n => (
          <Box key={n} sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" label={`Session ${n} name`} value={sessions[n].name} onChange={setS(n, 'name')} inputProps={{ maxLength: 40 }} sx={{ flex: 1 }} />
            <TextField size="small" label="Time" placeholder="e.g. 9:30–10:45" value={sessions[n].time} onChange={setS(n, 'time')} inputProps={{ maxLength: 40 }} sx={{ flex: 1 }} />
          </Box>
        ))}
      </Box>
      <FormGroup sx={{ mt: 1.5 }}>
        <FormControlLabel control={<Switch checked={rules.styleSpread} onChange={e => setRules(r => ({ ...r, styleSpread: e.target.checked }))} />}
          label={<Typography variant="body2">Keep styles apart — warn when two workshops of the same style are in one session</Typography>} />
        <FormControlLabel control={<Switch checked={rules.sameSpace} onChange={e => setRules(r => ({ ...r, sameSpace: e.target.checked }))} />}
          label={<Typography variant="body2">Same space both sessions — prefer keeping a leader in one room when they teach twice</Typography>} />
      </FormGroup>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button variant="contained" size="small" onClick={save} disabled={busy}>{busy ? <CircularProgress size={18} color="inherit" /> : 'Save'}</Button>
      </Box>
    </Paper>
  );
}

// A workshop's space rules (saved to the workshop right away), plus — when
// opened from a placed card — a seat limit for that one run (saved with the
// schedule).
function RulesDialog({ workshop: w, run, spaces, sessions, onClose, onSavedRules, onOverride }) {
  const [requiredSpace, setRequiredSpace] = useState(spaces.some(s => s.spaceId === w.requiredSpace) ? w.requiredSpace : (spaces.find(s => s.name.toLowerCase() === String(w.requiredSpace || '').toLowerCase())?.spaceId || ''));
  const [avoid, setAvoid] = useState((w.avoidSpaces || []).map(a => spaces.find(s => s.spaceId === a || s.name.toLowerCase() === a.toLowerCase())?.spaceId).filter(Boolean));
  const [override, setOverride] = useState(run?.capacityOverride || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const space = run && spaces.find(s => s.spaceId === run.spaceId);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const fields = { requiredSpace, avoidSpaces: avoid.filter(a => a !== requiredSpace) };
      const rulesChanged = fields.requiredSpace !== (w.requiredSpace || '') || fields.avoidSpaces.join() !== (w.avoidSpaces || []).join();
      if (rulesChanged) {
        await adminClient.saveWorkshop(w.workshopId, {
          title: w.title, style: w.style, description: w.description, equipmentNeeded: w.equipmentNeeded,
          maxDancers: w.maxDancers || '', ...fields,
        });
        onSavedRules(fields);
      }
      if (run) onOverride(Number(override) > 0 ? Math.round(Number(override)) : null);
      onClose();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{w.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {[w.leaderName, w.style, ALLOWED_SESSIONS_LABELS[w.allowedSessions], w.maxDancers ? `wants up to ${w.maxDancers}` : null,
            w.equipmentNeeded?.length ? `needs ${w.equipmentNeeded.join(', ')}` : null].filter(Boolean).join(' · ')}
          {run && ` · scheduled in ${space?.name}, ${sessions[run.session].name}`}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControl size="small" fullWidth>
          <InputLabel>Must be in</InputLabel>
          <Select label="Must be in" value={requiredSpace} onChange={e => setRequiredSpace(e.target.value)}>
            <MenuItem value=""><em>Any space</em></MenuItem>
            {spaces.map(s => <MenuItem key={s.spaceId} value={s.spaceId}>{s.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Must not be in</Typography>
        <FormGroup row>
          {spaces.filter(s => s.spaceId !== requiredSpace).map(s => (
            <FormControlLabel key={s.spaceId}
              control={<Checkbox size="small" checked={avoid.includes(s.spaceId)} onChange={() => setAvoid(a => (a.includes(s.spaceId) ? a.filter(x => x !== s.spaceId) : [...a, s.spaceId]))} />}
              label={<Typography variant="body2">{s.name}</Typography>} />
          ))}
        </FormGroup>
        {run && (
          <TextField size="small" type="number" label="Seat limit for this time slot (optional)" value={override}
            onChange={e => setOverride(e.target.value)} inputProps={{ min: 1 }} sx={{ mt: 2 }}
            helperText={`Seats are normally the smaller of the room (${space?.maxDancers}) and the workshop’s maximum. Saved with the schedule.`} />
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          The leader’s available sessions and the workshop’s details are edited on the Workshop Leaders page.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>{busy ? <CircularProgress size={18} color="inherit" /> : 'Done'}</Button>
      </DialogActions>
    </Dialog>
  );
}
